import { Show, TextArea } from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { SectionCard } from "shared/app/SectionCard";
import { PropertyKind } from "shared/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { type Register } from "shared/hooks";
import { useAuthContext } from "providers/Auth";

interface UnitGeneralFormProps {
  register: Register;
  headerExtra?: React.ReactNode;
  footer?: React.ReactNode;
  propertyKind?: PropertyKind | null;
  pmsId?: string;
  showAddress?: boolean;
}

export const UnitGeneralForm = (props: UnitGeneralFormProps) => {
  const { register, headerExtra, footer, propertyKind, pmsId, showAddress } =
    props;
  const dictionary = useUnitsPropertiesDictionary();
  const { type } = useAuthContext();

  return (
    <SectionCard title="General" headerExtra={headerExtra} footer={footer}>
      <div className="divide-y divide-alpha-40">
        <div className="grid grid-cols-2 gap-4 py-4">
          <Tooltip>
            <TooltipTrigger className="w-full text-left">
              <BaseInput
                id="title"
                label={`${dictionary.Unit} name`}
                placeholder={`${dictionary.Unit} 1`}
                value={register("title").value ?? ""}
                onChange={(val) => register("title").onChange(val)}
                error={register("title").error}
                required
                readOnly={Boolean(pmsId)}
              />
            </TooltipTrigger>
            {pmsId && (
              <TooltipContent className="max-w-[320px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                {dictionary.Unit} name can only be edited within the PMS
              </TooltipContent>
            )}
          </Tooltip>
          <div>
            <BaseInput
              id="externalId"
              label={`${dictionary.Unit} ID`}
              placeholder={`${dictionary.Unit} 1`}
              value={register("externalId").value ?? ""}
              onChange={(val) => register("externalId").onChange(val)}
              error={register("externalId").error}
              readOnly={Boolean(pmsId)}
            />
          </div>
        </div>

        <Show if={showAddress && type.isAddressValidationEnabled}>
          <div className="space-y-4 py-4">
            <p className="text-sm text-content-700">
              Leave blank to use the property&apos;s address for this{" "}
              {dictionary.Unit.toLowerCase()}.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <BaseInput
                id="address1"
                label="Street address"
                value={register("address1").value ?? ""}
                onChange={(val) => register("address1").onChange(val)}
                error={register("address1").error}
              />
              <BaseInput
                id="address2"
                label="Apartment, suite, etc"
                value={register("address2").value ?? ""}
                onChange={(val) => register("address2").onChange(val)}
                error={register("address2").error}
              />
              <BaseInput
                id="city"
                label="City"
                value={register("city").value ?? ""}
                onChange={(val) => register("city").onChange(val)}
                error={register("city").error}
              />
              <BaseInput
                id="state_or_province"
                label="State"
                value={register("state_or_province").value ?? ""}
                onChange={(val) => register("state_or_province").onChange(val)}
                error={register("state_or_province").error}
              />
              <BaseInput
                id="postal_code"
                label="Zip"
                value={register("postal_code").value ?? ""}
                onChange={(val) => register("postal_code").onChange(val)}
                error={register("postal_code").error}
              />
            </div>
          </div>
        </Show>

        <Show if={propertyKind || type.isPmsListingSyncEnabled}>
          <div className="py-4">
            <TextArea
              id="description"
              label="Description"
              placeholder="240"
              rows={4}
              autoExpand={true}
              readOnly={type.isPmsListingSyncEnabled && Boolean(pmsId)}
              {...register("description")}
            />
          </div>
        </Show>
      </div>
    </SectionCard>
  );
};
